/* Separate locally stocked units from confirmed supplier-order quantities. */
function lxpIsSpecialLine(item) {
  return item.properties?._lxp_fulfillment === 'special_order' || /^Special order/i.test(item.properties?.['Order type'] || '');
}
function lxpOrderAllocation(stock, quantity, items, variantId) {
  const inCart = items.filter(item => String(item.variant_id) === String(variantId) && !lxpIsSpecialLine(item))
    .reduce((total, item) => total + item.quantity, 0);
  const available = Math.max(0, stock - inCart);
  const stocked = Math.min(quantity, available);
  return { stock, inCart, stocked, special: quantity - stocked };
}
class LxpOrderData extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.variants = JSON.parse(this.querySelector('script').textContent);
    this.update = this.update.bind(this);
    this.form?.addEventListener('variant:change', this.update);
    this.form?.addEventListener('change', this.update);
    this.update();
  }
  disconnectedCallback() {
    this.form?.removeEventListener('variant:change', this.update);
    this.form?.removeEventListener('change', this.update);
  }
  current() {
    const id = this.form?.elements.namedItem('id')?.value;
    const quantity = Number(this.form?.elements.namedItem('quantity')?.value || 1);
    const variant = this.variants.find(item => String(item.id) === String(id));
    const physical = variant && this.dataset.giftCard !== 'true' && variant.shipping && variant.policy === 'continue';
    const stock = variant?.tracked ? Math.max(0, variant.quantity) : 0;
    return { id, quantity, variant, physical, stock };
  }
  update() {
    const current = this.current();
    this.querySelector('input').disabled = !(current.physical && current.stock === 0);
    const wrapper = this.closest('[data-lxp-purchase]');
    if (wrapper) {
      wrapper.dataset.lxpSpecial = String(current.physical && current.stock === 0);
      wrapper.dataset.lxpSupplierOrdering = String(Boolean(current.physical));
    }
    return current;
  }
}
customElements.define('lxp-order-data', LxpOrderData);

(() => {
  const drawer = document.getElementById('SpecialOrderNotice');
  if (!drawer) return;
  const continueButton = drawer.querySelector('[data-order-continue]');
  const error = drawer.querySelector('[data-order-error]');
  const description = drawer.querySelector('#SpecialOrderDescription');
  const defaultDescription = description.textContent;
  let pending = null;
  let busy = false;
  let approvedForm = null;
  const approvedInputs = new WeakSet();
  const root = window.Shopify?.routes?.root || '/';

  async function readCart() {
    const response = await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Couldn’t check your cart. Please try again.');
    return response.json();
  }
  function showError(message, request) {
    if (drawer.open) { error.textContent = message; error.hidden = false; }
    else if (request.form?.handleErrorMessage) request.form.handleErrorMessage(message);
    else {
      const container = request.input?.closest('cart-items');
      if (container?.onCartError) container.onCartError(message, request.input);
    }
  }
  function describe(request) {
    const plan = request.plan;
    drawer.querySelector('[data-order-product]').textContent = request.title;
    drawer.querySelector('[data-order-variant]').textContent = request.variantTitle === 'Default Title' ? '' : request.variantTitle;
    const summary = drawer.querySelector('[data-order-stock-summary]');
    summary.hidden = plan.stock === 0;
    summary.textContent = plan.stock > 0
      ? `Only ${plan.stock} ${plan.stock === 1 ? 'unit is' : 'units are'} in stock.${plan.inCart ? ` ${plan.inCart} ${plan.inCart === 1 ? 'is' : 'are'} already in your cart.` : ''} ${plan.stocked ? `${plan.stocked} of the units you’re adding will come from stock. ` : ''}The additional ${plan.special} ${plan.special === 1 ? 'unit will be a Special Order' : 'units will be Special Order items'}.`
      : '';
    description.textContent = plan.stock > 0 ? 'Special Order units are ordered from our supplier and take longer to arrive.' : defaultDescription;
    continueButton.querySelector('.btn-text').textContent = plan.stock > 0 ? `Add ${plan.special} as Special Order${plan.stocked ? ` + ${plan.stocked} from stock` : ''}` : 'Continue to add to cart';
    error.hidden = true;
  }
  function open(request) {
    pending = request;
    describe(request);
    continueButton.disabled = false;
    drawer.show(request.submitter || request.input);
  }
  function resumeForm(request) {
    approvedForm = request.form;
    try {
      const button = request.submitter;
      request.form.requestSubmit(button?.form === request.form && !button.disabled ? button : undefined);
    } finally { approvedForm = null; }
  }
  function resumeChange(input, quantity) {
    approvedInputs.add(input);
    input.value = quantity;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    approvedInputs.delete(input);
  }
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form === approvedForm) return;
    const metadata = form.querySelector?.('lxp-order-data');
    if (!metadata) return;
    const current = metadata.update();
    if (!current.physical || !Number.isInteger(current.quantity) || current.quantity < 1) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || drawer.open) return;
    const request = { form, metadata, ...current, submitter: event.submitter,
      title: metadata.dataset.productTitle, variantTitle: current.variant.title,
      parentModal: form.closest('quick-view, x-modal, cart-drawer') };
    busy = true;
    request.submitter?.setAttribute('aria-busy', 'true');
    try {
      const cart = await readCart();
      const latest = metadata.current();
      if (!form.isConnected || latest.id !== request.id || latest.quantity !== request.quantity) return;
      request.plan = lxpOrderAllocation(request.stock, request.quantity, cart.items, request.id);
      if (request.plan.special) open(request);
      else resumeForm(request);
    } catch (failure) { showError(failure.message, request); }
    finally { busy = false; request.submitter?.removeAttribute('aria-busy'); }
  }, true);

  document.addEventListener('change', async event => {
    const input = event.target.closest?.('input[data-lxp-order-stock]');
    if (!input || approvedInputs.has(input)) return;
    const requested = Number(input.value);
    const previous = Number(input.defaultValue);
    if (!Number.isInteger(requested) || requested <= previous || !input.checkValidity()) return;
    event.stopImmediatePropagation();
    input.value = previous;
    if (busy || drawer.open) return;
    const request = { input, requested, id: input.dataset.quantityVariantId, key: input.dataset.index,
      stock: Number(input.dataset.lxpOrderStock), parentModal: input.closest('cart-drawer') };
    busy = true;
    try {
      const cart = await readCart();
      const line = cart.items.find(item => item.key === request.key);
      if (!line || !input.isConnected) return;
      if (lxpIsSpecialLine(line) || requested <= line.quantity) { resumeChange(input, requested); return; }
      request.quantity = requested - line.quantity;
      request.line = line;
      request.title = line.product_title;
      request.variantTitle = line.variant_title || '';
      request.plan = lxpOrderAllocation(request.stock, request.quantity, cart.items, request.id);
      if (request.plan.special) open(request);
      else resumeChange(input, requested);
    } catch (failure) { showError(failure.message, request); }
    finally { busy = false; }
  }, true);

  drawer.addEventListener('drawer:afterHide', () => {
    const parent = pending?.parentModal;
    pending = null;
    if (parent?.open) setTimeout(() => theme.a11y.trapFocus(parent, parent.focusElement));
  });

  function buildItems(request) {
    let properties = {}, sellingPlan;
    if (request.form) {
      const fields = new FormData(request.form);
      for (const [key, value] of fields.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);
        if (match) properties[match[1]] = value;
      }
      sellingPlan = fields.get('selling_plan');
    } else {
      properties = { ...request.line.properties };
      sellingPlan = request.line.selling_plan_allocation?.selling_plan?.id;
    }
    delete properties['Order type']; delete properties._lxp_fulfillment;
    const base = { id: request.id, properties, ...(sellingPlan ? { selling_plan: sellingPlan } : {}) };
    const items = [];
    if (request.plan.stocked) items.push({ ...base, quantity: request.plan.stocked });
    if (request.plan.special) items.push({ ...base, quantity: request.plan.special,
      properties: { ...properties, 'Order type': drawer.dataset.orderProperty, _lxp_fulfillment: 'special_order' } });
    // Preserve the theme's optional product add-ons when the primary item is split.
    for (const addon of request.form?.bundles || []) items.push({ id: addon.value, quantity: 1 });
    return items;
  }
  async function add(request) {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: buildItems(request), sections: [...new Set(sections)], sections_url: location.pathname })
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || 'Couldn’t add this item. Please try again.');
    // The add succeeded: close the notice before opening/refreshing the cart.
    await drawer.hide();
    await new Promise(resolve => setTimeout(resolve, 0));
    try {
      const cart = await readCart();
      cart.sections = result.sections;
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'special-order', cart });
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: result } }));
    } catch {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    }
    if (!document.body.classList.contains('template-cart')) {
      if (theme.settings.cartType === 'page') location.assign(theme.routes.cart_url);
      else document.querySelector('cart-drawer')?.show(request.submitter);
    }
  }
  continueButton.addEventListener('click', async () => {
    if (!pending || busy) return;
    const request = pending;
    busy = true; continueButton.disabled = true; error.hidden = true;
    try {
      if (request.form) {
        const current = request.metadata.current();
        if (!request.form.isConnected || current.id !== request.id || current.quantity !== request.quantity) {
          await drawer.hide(); return;
        }
      }
      const cart = await readCart();
      if (pending !== request || !drawer.open) return;
      if (request.input) {
        const line = cart.items.find(item => item.key === request.key);
        if (!line || request.requested <= line.quantity) { await drawer.hide(); return; }
        request.quantity = request.requested - line.quantity;
        request.line = line;
      }
      const plan = lxpOrderAllocation(request.stock, request.quantity, cart.items, request.id);
      if (plan.special > request.plan.special) {
        request.plan = plan; describe(request); return;
      }
      request.plan = plan;
      await add(request);
    } catch (failure) { showError(failure.message, request); }
    finally { busy = false; continueButton.disabled = false; }
  });
})();
